import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { DepreciationReport, DepreciationAsset, DepreciationSummary } from "../../../types";
import { formatMoneyKzt } from "@/features/accounting/shared";
import { toNumber } from "@/shared/utils";
import { Skeleton, Table } from "@/shared/ui";
import { usePagination } from "@/shared/hooks";
import { Pagination } from "@/shared/components";
import { useGetWarehouseItemsQuery, useGetWarehouseUnitsQuery } from "../../../api";
import type { TransactionStatus } from "../../../types";

interface DepreciationTableProps {
  data?: DepreciationReport;
  isLoading?: boolean;
  error?: unknown;
}

export default function DepreciationTable({ data, isLoading, error }: DepreciationTableProps) {
  const { t } = useTranslation("DepreciationPage");

  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const { data: itemsData, isLoading: isLoadingItems } = useGetWarehouseItemsQuery();
  const { data: unitsData, isLoading: isLoadingUnits } = useGetWarehouseUnitsQuery();

  const itemStatusMap = useMemo(() => {
    if (!itemsData || !Array.isArray(itemsData)) return new Map<string, TransactionStatus>();
    const map = new Map<string, TransactionStatus>();
    itemsData.forEach((item) => {
      const key = `${item.name}|${item.vendor_name}`;
      map.set(key, item.transaction_status);
    });
    return map;
  }, [itemsData]);

  const filteredUnits = useMemo(() => {
    if (!unitsData || !Array.isArray(unitsData)) return [];
    if (isLoadingItems) return [];
    if (!itemsData || itemStatusMap.size === 0) return [];

    return unitsData.filter((unit) => {
      const vendorName = unit.vendor?.name || "";
      const key = `${unit.item_name}|${vendorName}`;
      const transactionStatus = itemStatusMap.get(key);

      if (transactionStatus === undefined) {
        return false;
      }

      return transactionStatus !== "draft";
    });
  }, [unitsData, itemStatusMap, isLoadingItems, itemsData]);

  const filteredUnitsMap = useMemo(() => {
    const map = new Map<number, boolean>();
    filteredUnits.forEach((unit) => {
      map.set(unit.id, true);
    });
    return map;
  }, [filteredUnits]);

  const filteredAssets = useMemo(() => {
    if (!data?.assets) return [];
    return data.assets.filter((asset) => filteredUnitsMap.has(asset.unit_id));
  }, [data?.assets, filteredUnitsMap]);

  const recalculatedSummary = useMemo((): DepreciationSummary | undefined => {
    if (!data?.summary) return undefined;

    const totalInitialCost = filteredAssets.reduce((sum, asset) => sum + toNumber(asset.initial_cost), 0);
    const totalResidualValue = filteredAssets.reduce((sum, asset) => sum + toNumber(asset.residual_value), 0);
    const totalMonthlyDepreciation = filteredAssets.reduce(
      (sum, asset) => sum + toNumber(asset.monthly_depreciation),
      0,
    );
    const totalAccumulatedDepreciation = filteredAssets.reduce(
      (sum, asset) => sum + toNumber(asset.accumulated_depreciation),
      0,
    );

    return {
      ...data.summary,
      total_assets_count: filteredAssets.length,
      total_initial_cost: totalInitialCost.toFixed(2),
      total_residual_value: totalResidualValue.toFixed(2),
      total_monthly_depreciation: totalMonthlyDepreciation.toFixed(2),
      total_accumulated_depreciation: totalAccumulatedDepreciation.toFixed(2),
    };
  }, [data?.summary, filteredAssets]);

  const startPeriodTotal = useMemo(() => {
    return filteredAssets
      .reduce((sum, asset) => sum + toNumber(asset.residual_value) + toNumber(asset.monthly_depreciation), 0)
      .toFixed(2);
  }, [filteredAssets]);

  const accumulatedTotal = useMemo(() => {
    return filteredAssets.reduce((sum, asset) => sum + toNumber(asset.accumulated_depreciation), 0).toFixed(2);
  }, [filteredAssets]);

  const combinedIsLoading = isLoading || isLoadingItems || isLoadingUnits;

  function handleSort(field: string) {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  }

  const sortedAssets = useMemo(() => {
    if (!sortField) return filteredAssets;
    return [...filteredAssets].sort((a, b) => {
      let aVal: number, bVal: number;
      switch (sortField) {
        case "initial_cost":
          aVal = toNumber(a.initial_cost);
          bVal = toNumber(b.initial_cost);
          break;
        case "start_period":
          aVal = toNumber(a.residual_value) + toNumber(a.monthly_depreciation);
          bVal = toNumber(b.residual_value) + toNumber(b.monthly_depreciation);
          break;
        case "cost_change":
          aVal = toNumber(a.accumulated_depreciation);
          bVal = toNumber(b.accumulated_depreciation);
          break;
        case "end_period":
          aVal = toNumber(a.residual_value);
          bVal = toNumber(b.residual_value);
          break;
        case "monthly":
          aVal = toNumber(a.monthly_depreciation);
          bVal = toNumber(b.monthly_depreciation);
          break;
        default:
          return 0;
      }
      return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
    });
  }, [filteredAssets, sortField, sortDirection]);

  const pagination = usePagination(sortedAssets.length, 10, [sortedAssets]);

  if (combinedIsLoading) {
    return (
      <div className="py-5 px-6 radius-3xl surface-base-fill">
        <div className="flex items-center gap-3 mb-6">
          <Skeleton width={300} height={24} />
        </div>
        <Skeleton width="100%" height={400} />
      </div>
    );
  }

  if (error || !data || !recalculatedSummary) {
    return (
      <div className="py-5 px-6 radius-3xl surface-base-fill">
        <div className="text-body-regular-md content-action-neutral">
          {error ? t("error") : t("noData")}
        </div>
      </div>
    );
  }

  const paginatedAssets = sortedAssets.slice(pagination.startIndex, pagination.endIndex);

  return (
    <div className="py-5 px-6 radius-3xl surface-base-fill">
      <div className="flex flex-col gap-4">
        {filteredAssets.length === 0 ? (
          <div className="text-body-regular-md content-action-neutral">{t("noData")}</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table.Table>
                <Table.Header>
                  <tr>
                    <Table.HeadCell>{t("assets.name")}</Table.HeadCell>
                    <Table.HeadCell
                      sortable
                      sortDirection={sortField === "initial_cost" ? sortDirection : undefined}
                      onSort={() => handleSort("initial_cost")}
                      align="right">
                      {t("assets.initialCost")}
                    </Table.HeadCell>
                    <Table.HeadCell
                      sortable
                      sortDirection={sortField === "start_period" ? sortDirection : undefined}
                      onSort={() => handleSort("start_period")}
                      align="right">
                      {t("assets.startPeriod")}
                    </Table.HeadCell>
                    <Table.HeadCell
                      sortable
                      sortDirection={sortField === "cost_change" ? sortDirection : undefined}
                      onSort={() => handleSort("cost_change")}
                      align="right">
                      {t("assets.costChange")}
                    </Table.HeadCell>
                    <Table.HeadCell
                      sortable
                      sortDirection={sortField === "end_period" ? sortDirection : undefined}
                      onSort={() => handleSort("end_period")}
                      align="right">
                      {t("assets.endPeriod")}
                    </Table.HeadCell>
                    <Table.HeadCell
                      sortable
                      sortDirection={sortField === "monthly" ? sortDirection : undefined}
                      onSort={() => handleSort("monthly")}
                      align="right">
                      {t("assets.monthlyDepreciation")}
                    </Table.HeadCell>
                  </tr>
                </Table.Header>
                <Table.Body>
                  {paginatedAssets.map((asset: DepreciationAsset) => {
                    const isFullyDepreciated = asset.is_fully_depreciated;
                    const startPeriodValue = toNumber(asset.residual_value) + toNumber(asset.monthly_depreciation);
                    return (
                      <Table.Row
                        key={`${asset.id}-${asset.unit_id}`}
                        className={isFullyDepreciated ? "opacity-60" : ""}>
                        <Table.Cell>
                          <div className="flex flex-col gap-0.5">
                            <span className="content-base-primary">{asset.name}</span>
                            {isFullyDepreciated && (
                              <span className="text-body-regular-xs content-action-neutral">
                                {t("assets.fullyDepreciated")}
                              </span>
                            )}
                          </div>
                        </Table.Cell>
                        <Table.Cell align="right">{formatMoneyKzt(asset.initial_cost)}</Table.Cell>
                        <Table.Cell align="right">{formatMoneyKzt(startPeriodValue)}</Table.Cell>
                        <Table.Cell align="right">{formatMoneyKzt(asset.accumulated_depreciation)}</Table.Cell>
                        <Table.Cell align="right">{formatMoneyKzt(asset.residual_value)}</Table.Cell>
                        <Table.Cell align="right">{formatMoneyKzt(asset.monthly_depreciation)}</Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table.Table>
            </div>
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              total={sortedAssets.length}
              fromItem={pagination.fromItem}
              toItem={pagination.toItem}
              onPageChange={pagination.setPage}
            />
            {recalculatedSummary && (
              <div className="mt-3">
                <Table.Table>
                  <Table.Body>
                    <Table.Row className="surface-component-fill">
                      <Table.Cell isBold className="rounded-l-2xl!">
                        {t("summary.footer", { count: recalculatedSummary.total_assets_count })}
                      </Table.Cell>
                      <Table.Cell align="right" isBold>
                        {formatMoneyKzt(recalculatedSummary.total_initial_cost)}
                      </Table.Cell>
                      <Table.Cell align="right" isBold>
                        {formatMoneyKzt(startPeriodTotal)}
                      </Table.Cell>
                      <Table.Cell align="right" isBold>
                        {formatMoneyKzt(accumulatedTotal)}
                      </Table.Cell>
                      <Table.Cell align="right" isBold>
                        {formatMoneyKzt(recalculatedSummary.total_residual_value)}
                      </Table.Cell>
                      <Table.Cell align="right" isBold className="rounded-r-2xl!">
                        {formatMoneyKzt(recalculatedSummary.total_monthly_depreciation)}
                      </Table.Cell>
                    </Table.Row>
                  </Table.Body>
                </Table.Table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
